import React, { Fragment } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import unionBy from 'lodash/unionBy';
import { SummarySectionV2 } from '@css-xp/base';
import { EntitlementDetails, LinkedDestinationEntitlement } from '@css-xp/graphtypes/src/generated-types-new';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { isJCAProduct } from '@atlassian/cssxp-utils-product';
import { EntitlementsCommonColumns } from '../entitlment-summary-utils';
import { PromotionsSummary } from '../promotions-summary';
import {
  UUIDSection,
  NextPriceEstimateSection,
  UsersSection,
  StatusSection,
  LastBilledUsersSection,
  SandboxParentSection,
  UserTier,
} from './sections';

export const EntitlementTemplate = ({
  entitlement,
  linkedEntitlements,
  refetch,
  appCount,
}: {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  appCount: number | undefined;
}) => {
  const { isApp, totalBilledUsers, productId, entitlementNumber } = entitlement;
  const activePromotions = entitlement?.promotions || [];
  const scheduledPromotions = entitlement?.scheduledChanges?.promotions || [];
  const promotions = unionBy(activePromotions, scheduledPromotions, 'promotionInstanceId')?.length || 0;

  return (
    <Fragment>
      <EntitlementsCommonColumns
        entitlement={entitlement as EntitlementDetails}
        linkedEntitlements={linkedEntitlements as []}
        refetch={refetch}
      />
      <UsersSection entitlement={entitlement} linkedEntitlements={linkedEntitlements as []} />
      <UserTier entitlement={entitlement} />
      <NextPriceEstimateSection entitlement={entitlement} />
      {isApp || (
        <SummarySectionV2.SummaryGridCalloutItem testId="summary-apps">
          <div className="actual-users">
            <label>Apps</label>

            {isJCAProduct(productId) && (
              <>
                <Tooltip position="top" tag="span" content="Could also be known as add-ons">
                  <InfoIcon label="info" size="small" testId="apps-info-icon" />
                </Tooltip>
              </>
            )}
            {<div id="appCount">{appCount ?? 0}</div>}
          </div>
        </SummarySectionV2.SummaryGridCalloutItem>
      )}
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-active-promotions">
        <PromotionsSummary entitlementNumber={entitlementNumber} promotions={promotions} />
      </SummarySectionV2.SummaryGridCalloutItem>
      <LastBilledUsersSection
        isApp={isApp}
        totalBilledUsers={totalBilledUsers}
        linkedEntitlements={linkedEntitlements as []}
      />
      <UUIDSection entitlement={entitlement} />
      <StatusSection entitlement={entitlement} />
      <SandboxParentSection entitlement={entitlement} />
    </Fragment>
  );
};
